import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, Home } from 'lucide-react';
import { useAuth } from '../App';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/for-schools', label: 'For Schools' },
    { path: '/for-pupils', label: 'For Pupils' },
    { path: '/for-parents', label: 'For Parents' },
    { path: '/pricing', label: 'Pricing' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo" aria-label="TypeTown Home">
            <Home className="w-8 h-8 text-coral-orange" aria-hidden="true" />
            <span className="font-display text-2xl font-bold">
              <span className="text-mint-green">Type</span>
              <span className="text-coral-orange">Town</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`font-bold text-sm transition-colors ${
                  isActive(link.path)
                    ? 'text-mint-green'
                    : 'text-text-medium hover:text-text-dark'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="font-bold bg-gradient-to-r from-mint-green to-sky-blue text-white px-6 py-2 rounded-2xl hover:scale-105 transition-all shadow-md"
                >
                  My Town
                </Link>
                <button
                  onClick={logout}
                  data-testid="nav-logout"
                  className="font-bold text-sm text-text-medium hover:text-coral-orange transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="font-bold text-sm text-text-medium hover:text-text-dark flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </Link>
                <Link
                  to="/login?mode=solo&signup=true"
                  data-testid="nav-trial-cta"
                  className="font-bold bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-6 py-2 rounded-2xl hover:scale-105 transition-all shadow-md"
                >
                  Start Free!
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-text-dark"
            data-testid="nav-mobile-toggle"
            aria-expanded={isOpen}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block font-bold text-sm py-2 ${
                  isActive(link.path) ? 'text-mint-green' : 'text-text-medium'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsOpen(false)}
                    className="block font-bold bg-gradient-to-r from-mint-green to-sky-blue text-white px-6 py-3 rounded-2xl text-center"
                  >
                    My Town
                  </Link>
                  <button
                    onClick={() => { logout(); setIsOpen(false); }}
                    className="block w-full font-bold text-sm text-coral-orange py-2 text-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="block font-bold text-sm text-text-medium py-2 text-center"
                  >
                    Login
                  </Link>
                  <Link
                    to="/login?mode=solo&signup=true"
                    onClick={() => setIsOpen(false)}
                    className="block w-full font-bold bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-6 py-3 rounded-2xl text-center"
                  >
                    Start Free!
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </nav>
    </header>
  );
};

export default Navbar;
